import { useState, useEffect } from 'react';

const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [ssoFailed, setSsoFailed] = useState(false); // Einlösen des SSO-Codes fehlgeschlagen -> Hinweis im Login

  // Session beim Start laden
  useEffect(() => {
    const loadStoredSession = async () => {
      try {
        // SSO aus Gineex Central: Einmal-Code aus dem Fragment (#sso=) einlösen; vorher aus Adresse/Verlauf entfernen.
        const ssoCode = window.location.hash.startsWith('#sso=') ? window.location.hash.slice(5) : null;
        if (ssoCode) {
          window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
          const res = await fetch('/api/auth/sso/exchange', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: ssoCode }),
          }).catch(() => null);
          if (res?.ok) {
            startSession(await res.json());
            return;
          }
          setSsoFailed(true);
        }

        const storedUser = localStorage.getItem('sap_oci_user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          
          // Token-Gültigkeit prüfen
          if (userData.token && isTokenValid(userData.token)) {
            // Server fragen: gesperrte/abgelöste Sitzungen nicht wiederbeleben (Netzfehler/Timeout -> wie bisher wiederherstellen)
            const check = await fetch('/api/auth/user/profile', {
              headers: { Authorization: `Bearer ${userData.token}` },
              signal: AbortSignal.timeout(5000),
            }).catch(() => null);
            if (check?.status === 401) {
              console.log('Stored session revoked by server, clearing storage');
              localStorage.removeItem('sap_oci_user');
              return;
            }
            console.log('Stored session found and valid, restoring user');
            setUser(userData);
          } else {
            console.log('Stored session expired, clearing storage');
            localStorage.removeItem('sap_oci_user');
          }
        }
      } catch (error) {
        console.error('Error loading stored session:', error);
        localStorage.removeItem('sap_oci_user');
      } finally {
        setIsLoading(false);
      }
    };

    loadStoredSession();
  }, []);

  // User-State direkt aktualisieren
  const updateUser = (updatedUser) => {
    const newUser = { ...user, ...updatedUser };
    setUser(newUser);
    try {
      localStorage.setItem('sap_oci_user', JSON.stringify(newUser));
    } catch (error) {
      console.error('Error updating user in localStorage:', error);
    }
  };

  // Token-Gültigkeit prüfen
  const isTokenValid = (token) => {
    try {
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      
      // Prüfen ob Token noch nicht abgelaufen ist (mit 5 Minuten Puffer)
      return tokenPayload.exp > (currentTime + 300);
    } catch (error) {
      console.error('Error validating token:', error);
      return false;
    }
  };

  const handleLogin = async (userData) => {
    try {
      console.log('Attempting login with:', { username: userData.username, password: '[HIDDEN]' });
      
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: userData.username,
          password: userData.password
        })
      });
      
      const loginData = await response.json();
      
      if (!response.ok) {
        throw new Error(loginData.error || `Login failed: ${response.status}`);
      }

      // Zweiter Schritt (SAAS-MODUS-DESIGN §5.2): Sitzung erst nach gültigem Code.
      if (loginData.mfaRequired) {
        return { mfa: { enrolled: !!loginData.enrolled, mfaToken: loginData.mfaToken } };
      }
      startSession(loginData);
      return { done: true };
    } catch (error) {
      console.error('Login error:', error);
      
      // Spezifische Fehlermeldungen basierend auf Backend-Response
      let errorMessage = 'Anmeldung fehlgeschlagen.';
      
      if (error.message.includes('fetch') || error.message.includes('NetworkError')) {
        errorMessage = 'Verbindungsfehler: Kann nicht mit dem Server verbinden.';
      } else if (error.message.includes('Administrator-Passwort')) {
        errorMessage = 'Falsches Administrator-Passwort.';
      } else if (error.message.includes('nicht gefunden')) {
        errorMessage = 'Benutzer nicht gefunden oder deaktiviert.';
      } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        errorMessage = 'Ungültige Anmeldedaten. Bitte überprüfen Sie Benutzername und Passwort.';
      } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
        errorMessage = 'Zugriff verweigert. Ihr Account ist möglicherweise deaktiviert.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  };

  // POST an /api/auth/mfa/* -> JSON. Fehler tragen i18nKey (Übersetzung in der Oberfläche, Server-Text
  // als Rückfall) und expired bei abgelaufenem Zwischen-Token.
  const MFA_ERROR_KEYS = { 401: 'login.mfa.invalidCode', 429: 'login.mfa.locked' };
  const postMfa = async (path, body) => {
    let res;
    try {
      res = await fetch(`/api/auth/mfa/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch {
      throw Object.assign(new Error('network'), { i18nKey: 'login.mfa.networkError' });
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const expired = data.error === 'Sitzung ungültig';
      const i18nKey = expired ? 'login.mfa.expired' : (MFA_ERROR_KEYS[res.status] || (data.error ? null : 'login.mfa.failed'));
      throw Object.assign(new Error(data.error || String(res.status)), { expired, i18nKey });
    }
    return data;
  };

  const setupMfa = (mfaToken) => postMfa('setup', { mfaToken });

  // Codes immer als String senden (Backend lehnt Zahlen ab). Bei Ersteinrichtung startet die
  // Sitzung erst über finish(), damit die Wiederherstellungscodes vorher angezeigt werden.
  const completeMfa = async ({ mfaToken, code, recoveryCode }) => {
    const body = { mfaToken };
    if (recoveryCode) body.recoveryCode = String(recoveryCode).trim();
    else body.code = String(code || '').replace(/\s/g, '');
    const data = await postMfa('verify', body);
    if (data.recoveryCodes) return { recoveryCodes: data.recoveryCodes, finish: () => startSession(data) };
    startSession(data);
    return {};
  };

  // Login-Antwort {token, user} -> Sitzung speichern und setzen (Format wie bisher).
  const startSession = (loginData) => {
    try {
      // Backend Response Format:
      // {
      //   "token": "jwt_token_here",
      //   "user": {
      //     "userId": 123,
      //     "username": "admin",
      //     "name": "Administrator", 
      //     "costCenter": "ADMIN",
      //     "role": "admin"
      //   }
      // }
      // 
      // User-ID aus Backend Response oder JWT-Token extrahieren
      let userId = loginData.user.userId; // Direkt aus Backend Response
      
      // Fallback: Aus JWT-Token extrahieren falls nicht in Response
      if (!userId) {
        try {
          const tokenPayload = JSON.parse(atob(loginData.token.split('.')[1]));
          userId = tokenPayload.userId;
        } catch (e) {
          console.warn('Could not extract userId from token');
        }
      }
      
      const userWithToken = {
        id: userId, // Aus Backend Response oder JWT-Token extrahiert
        username: loginData.user.username,
        firstName: loginData.user.firstName,
        lastName: loginData.user.lastName,
        email: loginData.user.email,
        department: loginData.user.department,
        plant: loginData.user.plant,
        name: loginData.user.name || loginData.user.username, // Fallback
        costCenter: loginData.user.costCenter || 'DEFAULT',
        purchasingOrg: loginData.user.purchasingOrg,
        purchasingGroup: loginData.user.purchasingGroup || '',
        sapUser: loginData.user.sapUser || '',
        requestor: loginData.user.requestor || '',
        role: loginData.user.role,
        isGineexSupport: !!loginData.user.isGineexSupport,
        token: loginData.token,
        logo: loginData.user.logo || null,
        loginTime: Date.now() // Für Session-Tracking
      };
      
      console.log('🐛 Debug: Login successful with userId:', userId, 'from backend/token');
      console.log('Login successful:', { ...userWithToken, token: '[HIDDEN]' });
      
      // Session in localStorage speichern
      try {
        localStorage.setItem('sap_oci_user', JSON.stringify(userWithToken));
        console.log('Session saved to localStorage');
      } catch (storageError) {
        console.warn('Could not save session to localStorage:', storageError);
      }
      
      setUser(userWithToken);
      setSsoFailed(false);
    } catch (error) {
      console.error('Session start error:', error);
      throw new Error('Anmeldung fehlgeschlagen.');
    }
  };

  const handleLogout = async (cart = [], ociMode = false, ociParams = null) => {
    // Spezielle Behandlung für OCI-Benutzer
    if (ociMode && ociParams) {
      console.log('🚀 OCI-Logout: Bereite SAP-Rückkehr vor');
      
      // Für OCI-Benutzer: Direkt zurück zu SAP ohne Bestätigung
      try {
        await performOciLogout(ociParams);
        
        // Session lokal löschen
        localStorage.removeItem('sap_oci_user');
        
        // Clear all BANF draft data for all users
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('banf_draft_')) {
            localStorage.removeItem(key);
          }
        });
        
        setUser(null);
        setSsoFailed(false);
        
        console.log('✅ OCI-Logout erfolgreich - Rückkehr zu SAP');
        return true;
      } catch (error) {
        console.error('❌ OCI-Logout fehlgeschlagen:', error);
        // Fallback: Normaler Logout
      }
    }
    
    // Normale Logout-Bestätigung wird jetzt über ein Modal in der App-Komponente gehandhabt
    // Diese Funktion wird nur noch aufgerufen, wenn die Bestätigung bereits erfolgt ist
    
    try {
      if (user?.token) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        });
        console.log('Logout API call successful');
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Logout trotzdem durchführen, auch wenn API-Call fehlschlägt
    }
    
    // Session aus localStorage entfernen
    try {
      localStorage.removeItem('sap_oci_user');
      
      // Clear all BANF draft data for all users
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('banf_draft_')) {
          localStorage.removeItem(key);
        }
      });
      
      console.log('Session and BANF drafts removed from localStorage');
    } catch (storageError) {
      console.warn('Could not remove session from localStorage:', storageError);
    }
    
    setUser(null);
    setSsoFailed(false);
    return true;
  };
  
  // OCI-spezifische Logout-Funktion
  const performOciLogout = async (ociParams) => {
    console.log('🔄 Führe OCI-Logout durch - Rückkehr zu SAP mit leeren Daten');
    
    // Prüfe ob SAP-Integration
    const isSapIntegration = ociParams?.hookUrl && (
      ociParams.hookUrl.includes('sap-cust://') || 
      ociParams.hookUrl.includes('SAPEVENT:') ||
      ociParams.hookUrl.includes('sap-place-holder')
    );
    
    if (isSapIntegration) {
      console.log('🚀 SAP-Integration erkannt - bereite sofortige Rückkehr vor');
      
      // Erstelle OCI-Form für SAP-Rückkehr mit leeren Daten
      const ociForm = document.createElement('form');
      ociForm.method = 'POST';
      ociForm.action = ociParams.hookUrl;
      ociForm.target = '_self'; // Im gleichen Fenster - kehrt zu SAP zurück
      ociForm.style.display = 'none';
      
      // SAP-Logout-Parameter (mit leeren Warenkorb-Daten)
      const logoutParams = [
        { name: '~OkCode', value: 'CNCL' }, // Cancel - wichtig für Logout
        { name: '~TARGET', value: '_self' },
        { name: '~CALLER', value: 'ProCureX' },
        { name: '~CLOSE_CATALOG', value: 'TRUE' },
        { name: '~RETURN_URL', value: 'BACK' },
        { name: 'NEW_ITEM-COUNT', value: '0' }, // Keine Artikel übertragen
        { name: 'HOOKLOGOUT', value: 'TRUE' } // Logout-Flag
      ];
      
      logoutParams.forEach(param => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = param.name;
        input.value = param.value;
        ociForm.appendChild(input);
      });
      
      // Form zur Seite hinzufügen und SOFORT submitten
      document.body.appendChild(ociForm);
      
      console.log('📤 Submitte OCI-Logout-Form SOFORT zur SAP-Hook-URL:', ociParams.hookUrl);
      try {
        ociForm.submit();
        console.log('✅ OCI-Logout erfolgreich an SAP gesendet - Browser kehrt zu SAP zurück!');
      } catch (error) {
        console.log('⚠️ OCI-Logout Form-Submit fehlgeschlagen:', error);
        // Fallback: Browser-Fenster schließen
        if (window.opener) {
          window.close();
        } else {
          // Fallback: Direkte Navigation zur Hook-URL
          window.location.href = ociParams.hookUrl;
        }
      }
      
    } else {
      // Für normale URLs: Browser-Fenster schließen oder Hook-URL aufrufen
      console.log('🔄 Standard OCI-Logout: Browser-Fenster schließen');
      
      if (window.opener) {
        // Pop-up-Fenster schließen
        window.close();
      } else if (ociParams.hookUrl) {
        // Zurück zur Hook-URL mit leeren Daten
        window.location.href = ociParams.hookUrl;
      } else {
        // Fallback: Browser-Fenster schließen
        window.close();
      }
    }
  };

  return {
    user,
    isLoading,
    ssoFailed,
    handleLogin,
    setupMfa,
    completeMfa,
    handleLogout,
    updateUser
  };
};

export default useAuth;